/**
 * Split Payment utilities.
 *
 * Allow booking cost to be split between multiple payers.
 *
 * Split config stored in transaction.protectedData.splitPayment = {
 *   primaryPayerPercent: number,   — % paid by the initiator
 *   splits: [{ email: string, percent: number, status: 'pending'|'paid' }]
 * }
 *
 * Constraints:
 *   - All percentages must sum to 100
 *   - Min split: 5%
 *   - Max co-payers: 10
 */

export const MAX_CO_PAYERS = 10;
export const MIN_SPLIT_PERCENT = 5;

/**
 * Validate split config.
 * @param {number} primaryPercent
 * @param {{ email: string, percent: number }[]} splits
 * @returns {string|null} — error message or null
 */
export const validateSplits = (primaryPercent, splits) => {
  if (!Array.isArray(splits)) return 'Splits must be an array';
  if (splits.length > MAX_CO_PAYERS) return `Maximum ${MAX_CO_PAYERS} co-payers`;
  if (primaryPercent < MIN_SPLIT_PERCENT) return `Primary payer must pay at least ${MIN_SPLIT_PERCENT}%`;

  for (const s of splits) {
    if (!s.email) return 'Each split must have an email';
    if (s.percent < MIN_SPLIT_PERCENT) return `Each split must be at least ${MIN_SPLIT_PERCENT}%`;
  }

  const total = primaryPercent + splits.reduce((sum, s) => sum + s.percent, 0);
  if (Math.round(total) !== 100) return `Percentages must sum to 100 (got ${total})`;
  return null;
};

/**
 * Calculate split amounts.
 * @param {number} totalCents
 * @param {number} primaryPercent
 * @param {{ email: string, percent: number }[]} splits
 * @returns {{ primaryCents: number, splits: { email: string, cents: number }[] }}
 */
export const calculateSplitAmounts = (totalCents, primaryPercent, splits) => {
  const primaryCents = Math.round(totalCents * (primaryPercent / 100));
  let remaining = totalCents - primaryCents;

  const splitAmounts = splits.map((s, i) => {
    const cents = i === splits.length - 1
      ? remaining
      : Math.round(totalCents * (s.percent / 100));
    remaining -= cents;
    return { email: s.email, cents, percent: s.percent };
  });

  return { primaryCents, splits: splitAmounts };
};

/**
 * Check if all splits are paid.
 * @param {Object} splitPayment — protectedData.splitPayment
 * @returns {boolean}
 */
export const isFullyPaid = (splitPayment) => {
  if (!splitPayment?.splits) return false;
  return splitPayment.splits.every(s => s.status === 'paid');
};
